import { useState, useMemo } from 'react';
import { X, CheckCircle, XCircle, Edit3, User, Building2, Calendar, FileText, Award, AlertCircle, ShieldCheck, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useProposedRecords, ProposedRecord } from '../../context/ProposedRecordsContext';
import { QRCodeCanvas } from '../shared/QRCodeCanvas';

interface ReviewProposalModalProps {
  record: ProposedRecord;
  onClose: () => void;
}

export function ReviewProposalModal({ record, onClose }: ReviewProposalModalProps) {
  const { updateRecordStatus, requestEdit, approveProposal, rejectProposal } = useProposedRecords();
  const [action, setAction] = useState<'approve' | 'reject' | 'edit' | null>(null);
  const [comment, setComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Parse data robustly — may be double-encoded JSON string from raw D1 SQL
  const parsedData = useMemo(() => {
    let d = record.data;
    if (!d) return {};
    // Try parsing up to 3 times (handles double/triple encoded JSON strings)
    for (let i = 0; i < 3; i++) {
      if (typeof d === 'string') {
        try { d = JSON.parse(d); } catch { break; }
      } else {
        break;
      }
    }
    if (typeof d === 'object' && d !== null) return d as Record<string, unknown>;
    return {};
  }, [record.data]);

  console.log('[ReviewProposalModal] record.data type:', typeof record.data, 'parsedData:', parsedData, 'isCert:', !!parsedData.certificateId);
  const isCertificate = !!parsedData.certificateId || !!parsedData.certificateNumber;

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await approveProposal(record.id, comment || undefined);
      toast.success('Record approved');
      setProcessing(false);
      onClose();
    } catch (err: any) {
      // Keep the modal open on failure so the user can see that the
      // approval didn't actually succeed. Previously we closed the modal
      // unconditionally, which made a 500 from the backend look identical
      // to a successful approval — the record would stay in "Pending
      // Review" with no explanation.
      console.error('Approval failed:', err);
      const message =
        err?.response?.data?.error ??
        err?.message ??
        'Failed to approve record';
      toast.error(message);
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      await rejectProposal(record.id, rejectionReason);
      toast.success('Record rejected');
      setProcessing(false);
      onClose();
    } catch (err: any) {
      console.error('Rejection failed:', err);
      const message =
        err?.response?.data?.error ??
        err?.message ??
        'Failed to reject record';
      toast.error(message);
      setProcessing(false);
    }
  };

  const handleRequestEdit = async () => {
    if (!comment.trim()) {
      alert('Please provide specific changes you\'d like to see');
      return;
    }
    setProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    requestEdit(record.id, comment);
    setProcessing(false);
    onClose();
  };

  const renderCertificatePreview = () => {
    const d = parsedData;
    const verifyUrl = `https://api-dev.petso.dev/verify/${d.certificateNumber || ''}`;
    return (
      <div className="space-y-4">
        {/* Certificate Visual Card */}
        <div style={{ width: '100%', background: '#fff', borderRadius: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          <div style={{ background: '#0f172a', padding: '16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Issued by</p>
              <p style={{ fontSize: '14px', fontWeight: 600, marginTop: '2px' }}>{d.issuedByTitle ? `${d.issuedByTitle} ` : ''}{d.issuedByName || record.professionalName}</p>
              <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{record.professionalBusinessName}</p>
            </div>
            <ShieldCheck style={{ color: '#34d399', width: '24px', height: '24px' }} />
          </div>
          <div style={{ background: '#f8fafc', padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Issued To</p>
              <p style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a' }}>{record.petName || '—'}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Document</p>
              <p style={{ fontSize: '12px', fontWeight: 500, color: '#0f172a' }}>{d.title || d.certificateType || record.recordType}</p>
            </div>
          </div>
          <div style={{ padding: '12px 16px' }}>
            {Object.entries(d).filter(([k, v]) => v && !['certificateId','certificateNumber','certificateType','title','issuedAt','issuedByName','issuedByTitle','documentHash','validFrom','validUntil','remarks','systems'].includes(k)).map(([key, value]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid #f1f5f9', padding: '5px 0' }}>
                <span style={{ color: '#64748b', textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                <span style={{ fontWeight: 500, color: '#1e293b', textAlign: 'right', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{Array.isArray(value) ? `${(value as string[]).length} items` : String(value)}</span>
              </div>
            ))}
            {d.remarks && (
              <div style={{ marginTop: '8px', background: '#fffbeb', padding: '8px', borderRadius: '6px', fontSize: '12px', color: '#92400e' }}><strong>Remarks:</strong> {d.remarks}</div>
            )}
          </div>
          <div style={{ padding: '8px 16px', borderTop: '1px solid #e2e8f0', fontSize: '11px' }}>
            {d.certificateNumber && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Certificate No.</span><span style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace', fontSize: '10px' }}>{d.certificateNumber}</span></div>}
            {d.issuedAt && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Issued</span><span style={{ fontWeight: 500, color: '#0f172a' }}>{new Date(d.issuedAt).toLocaleDateString()}</span></div>}
            {d.validFrom && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Valid From</span><span style={{ fontWeight: 500, color: '#0f172a' }}>{d.validFrom}</span></div>}
            {d.validUntil && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Valid Until</span><span style={{ fontWeight: 500, color: '#0f172a' }}>{d.validUntil}</span></div>}
          </div>
          <div style={{ background: '#ecfdf5', padding: '12px 16px', borderTop: '1px solid #a7f3d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: '9px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '3px' }}><ShieldCheck style={{ width: '10px', height: '10px' }} /> Cryptographically Verified</p>
              <p style={{ fontSize: '8px', color: '#047857', fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>{d.documentHash ? String(d.documentHash).slice(0, 32) + '...' : d.certificateNumber}</p>
            </div>
            <div style={{ background: '#fff', padding: '4px', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
              <QRCodeCanvas value={verifyUrl} size={48} />
            </div>
          </div>
        </div>

        {/* Certificate Details */}
        <div className="space-y-2 text-sm">
          {d.certificateNumber && (
            <div className="flex justify-between"><span className="text-gray-500">Certificate No.</span><span className="font-mono text-gray-900">{d.certificateNumber}</span></div>
          )}
          {d.issuedAt && (
            <div className="flex justify-between"><span className="text-gray-500">Issued</span><span className="text-gray-900">{new Date(d.issuedAt).toLocaleDateString()}</span></div>
          )}
          {d.validFrom && (
            <div className="flex justify-between"><span className="text-gray-500">Valid From</span><span className="text-gray-900">{d.validFrom}</span></div>
          )}
          {d.validUntil && (
            <div className="flex justify-between"><span className="text-gray-500">Valid Until</span><span className="text-gray-900">{d.validUntil}</span></div>
          )}
        </div>

        {/* Verification Link */}
        {d.certificateNumber && (
          <a href={verifyUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700">
            <ExternalLink className="w-4 h-4" />
            Verify certificate publicly
          </a>
        )}
      </div>
    );
  };

  const renderRecordDetails = () => {
    // If this is a certificate-based proposed record, show certificate preview
    if (isCertificate) return renderCertificatePreview();

    const d = parsedData;
    switch (record.recordType) {
      case 'vaccine':
        return (
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Vaccine Name</label>
              <div className="text-gray-900">{d.vaccineName}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Date Administered</label>
                <div className="text-gray-900">{d.recommendedDate || d.dateAdministered || '—'}</div>
              </div>
              {(d.expiryDate || d.validUntil) && (
                <div>
                  <label className="text-sm text-gray-600">Expiry/Valid Until</label>
                  <div className="text-gray-900">{d.expiryDate || d.validUntil}</div>
                </div>
              )}
            </div>
            {(d.batch || d.batchNumber) && (
              <div>
                <label className="text-sm text-gray-600">Batch Number</label>
                <div className="text-gray-900 font-mono text-sm">{d.batch || d.batchNumber}</div>
              </div>
            )}
            {d.manufacturer && (
              <div>
                <label className="text-sm text-gray-600">Manufacturer</label>
                <div className="text-gray-900">{d.manufacturer}</div>
              </div>
            )}
            {d.notes && (
              <div>
                <label className="text-sm text-gray-600">Notes</label>
                <div className="text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">{d.notes}</div>
              </div>
            )}
          </div>
        );

      case 'grooming':
        return (
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Service</label>
              <div className="text-gray-900">{d.serviceName || d.title || '—'}</div>
            </div>
            {d.services && Array.isArray(d.services) && (
              <div>
                <label className="text-sm text-gray-600">Services Provided</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {d.services.map((s: string, i: number) => (
                    <span key={i} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {d.coatCondition && (
              <div>
                <label className="text-sm text-gray-600">Coat Condition</label>
                <div className="text-gray-900">{d.coatCondition}</div>
              </div>
            )}
            {d.notes && (
              <div>
                <label className="text-sm text-gray-600">Notes</label>
                <div className="text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">{d.notes}</div>
              </div>
            )}
          </div>
        );

      case 'treatment':
      case 'medical':
        return (
          <div className="space-y-3">
            {(d.treatmentName || d.title) && (
              <div>
                <label className="text-sm text-gray-600">Treatment</label>
                <div className="text-gray-900">{d.treatmentName || d.title}</div>
              </div>
            )}
            {d.diagnosis && (
              <div>
                <label className="text-sm text-gray-600">Diagnosis</label>
                <div className="text-gray-900">{d.diagnosis}</div>
              </div>
            )}
            {d.medication && (
              <div>
                <label className="text-sm text-gray-600">Medication</label>
                <div className="text-gray-900">{d.medication}</div>
              </div>
            )}
            {d.weight && (
              <div>
                <label className="text-sm text-gray-600">Weight</label>
                <div className="text-gray-900">{d.weight} kg</div>
              </div>
            )}
            {d.temperature && (
              <div>
                <label className="text-sm text-gray-600">Temperature</label>
                <div className="text-gray-900">{d.temperature} °C</div>
              </div>
            )}
            {d.notes && (
              <div>
                <label className="text-sm text-gray-600">Notes</label>
                <div className="text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">{d.notes}</div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="space-y-2">
            {Object.entries(d).filter(([, v]) => v != null && v !== '').map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                <span className="text-gray-900 text-right max-w-[250px] truncate">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
              </div>
            ))}
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl mb-1">Review Proposed Record</h2>
            <p className="text-blue-100 text-sm">You are in control - approve, reject, or request changes</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Professional Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl flex-shrink-0">
                {record.professionalName.charAt(0)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">{record.professionalName}</span>
                  <span className="text-sm text-gray-600">({record.professionalType})</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Building2 className="w-4 h-4" />
                  <span>{record.professionalBusinessName}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                  <Calendar className="w-4 h-4" />
                  <span>Submitted on {new Date(record.submittedDate).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pet Info */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">🐕</span>
              <span className="font-medium">{record.petName}</span>
            </div>
            <div className="text-sm text-gray-600 font-mono break-all">{record.petDID}</div>
          </div>

          {/* Record Type Badge */}
          <div className="flex items-center gap-2">
            <span className="text-3xl">
              {record.recordType === 'vaccine' && '💉'}
              {record.recordType === 'grooming' && '✂️'}
              {record.recordType === 'treatment' && '🏥'}
              {record.recordType === 'document' && '📄'}
              {record.recordType === 'medical' && '⚕️'}
              {record.recordType === 'note' && '📝'}
            </span>
            <div>
              <div className="text-sm text-gray-600">Record Type</div>
              <div className="text-lg font-medium capitalize">{record.recordType}</div>
            </div>
          </div>

          {/* Record Details */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-600" />
              Proposed Information
            </h3>
            {renderRecordDetails()}
          </div>

          {/* Supporting Documents */}
          {record.supportingDocuments && record.supportingDocuments.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-medium mb-2">Supporting Documents</h3>
              <div className="space-y-2">
                {record.supportingDocuments.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-blue-600">
                    <FileText className="w-4 h-4" />
                    <span>{doc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Impact Notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-900">
              <p className="font-medium mb-1">What happens if you approve?</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>This record will be added to {record.petName}'s official profile</li>
                <li>All authorized users with access to {record.petName}'s DID will see this update in real-time</li>
                <li>The record will be secured on the blockchain with full audit trail</li>
                <li>Both you and {record.professionalName} will earn PETs tokens for maintaining accurate records</li>
              </ul>
            </div>
          </div>

          {/* Action Selection */}
          {!action && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-medium mb-4">Choose an action:</h3>
              <div className="grid gap-3">
                <button
                  onClick={() => setAction('approve')}
                  className="flex items-center gap-3 p-4 border-2 border-green-200 rounded-lg hover:bg-green-50 transition-colors text-left"
                >
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-green-900">Approve</div>
                    <div className="text-sm text-gray-600">Add this record to {record.petName}'s profile</div>
                  </div>
                </button>
                <button
                  onClick={() => setAction('edit')}
                  className="flex items-center gap-3 p-4 border-2 border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
                >
                  <Edit3 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-blue-900">Request Edit</div>
                    <div className="text-sm text-gray-600">Ask the professional to modify this proposal</div>
                  </div>
                </button>
                <button
                  onClick={() => setAction('reject')}
                  className="flex items-center gap-3 p-4 border-2 border-red-200 rounded-lg hover:bg-red-50 transition-colors text-left"
                >
                  <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-red-900">Reject</div>
                    <div className="text-sm text-gray-600">Decline this proposed record</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Approve Form */}
          {action === 'approve' && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-medium mb-4 text-green-900">Approve Record</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Add a comment (optional)
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows={3}
                    placeholder="E.g., Thanks for keeping this updated!"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleApprove}
                    disabled={processing}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle className="w-5 h-5" />
                    {processing ? 'Processing...' : 'Confirm Approval'}
                  </button>
                  <button
                    onClick={() => setAction(null)}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Request Edit Form */}
          {action === 'edit' && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-medium mb-4 text-blue-900">Request Edit</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    What changes would you like? *
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={4}
                    placeholder="Please be specific about what needs to be changed..."
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleRequestEdit}
                    disabled={processing}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <Edit3 className="w-5 h-5" />
                    {processing ? 'Sending...' : 'Send Edit Request'}
                  </button>
                  <button
                    onClick={() => setAction(null)}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reject Form */}
          {action === 'reject' && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-medium mb-4 text-red-900">Reject Record</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Reason for rejection *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows={3}
                    placeholder="E.g., This information is incorrect, or not applicable..."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Additional comment (optional)
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows={2}
                    placeholder="Any additional context..."
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleReject}
                    disabled={processing}
                    className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    <XCircle className="w-5 h-5" />
                    {processing ? 'Processing...' : 'Confirm Rejection'}
                  </button>
                  <button
                    onClick={() => setAction(null)}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
